import type { OwaDate } from 'owa-datetime';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { getPageIndex } from './pageUtils';
import type { ScheduleItem } from 'owa-graph-schema';
import { tryGetFreeBusy } from './tryGetFreeBusy';
import { convertScheduleItemToCalendarEvent } from 'owa-calendar-scheduleitem-converters';
import { scaleRangeAsCacheSize } from './scaleRangeAsCacheSize';

/**
 * Gets an array of free busy events for an attendee between the start and end time
 * converted to the given time zone
 * @param sessionId Id for this free/busy cache session
 * @param attendee The SMTP address for which free busy is requested
 * @param start The start time for which to retrieve free busy
 * @param end The end time for which to retrieve free busy
 * @param timezoneToSet An optional time zone for events to be converted to
 */
export function getAttendeeDetailedEvents(
    sessionId: string,
    attendee: string,
    start: OwaDate,
    end: OwaDate,
    timezoneToSet?: string
): CalendarEvent[] {
    const tz = timezoneToSet || start.tz;
    const updatedEnd = scaleRangeAsCacheSize(start, end);
    const startPageIndex = getPageIndex(start);
    const endPageIndex = getPageIndex(updatedEnd);
    const events: CalendarEvent[] = [];

    for (let index = startPageIndex; index <= endPageIndex; index++) {
        const page = tryGetFreeBusy(sessionId, attendee, index);

        if (page) {
            const pageEvents = ((page.scheduleItems as ScheduleItem[]) || [])
                .filter(e => e.status != 'Free')
                .map(item => {
                    return {
                        ...convertScheduleItemToCalendarEvent(item, page.scheduleId, tz),
                        IsFreeBusyOnly: !item.subject, // we assume if there's no subject we don't have details
                    } as CalendarEvent;
                });

            const filteredEvents: CalendarEvent[] = pageEvents.filter(
                (event): event is CalendarEvent => {
                    return event !== null;
                }
            );

            events.push(...filteredEvents);
        }
    }

    return events;
}
